"use client";

/**
 * Fascia multimediale generica.
 *
 * Mostra una sequenza di media eterogenei (video, foto, gallery, audio)
 * con tre layout possibili:
 *   - "strip":    riga orizzontale scorrevole (snap, scroll touch)
 *   - "grid":     griglia responsive (2/3/4 colonne configurabili)
 *   - "carousel": un item alla volta con frecce/indicatori (ideale per
 *                 raccogliere contenuti ricchi alternati)
 *
 * È usato dal PageBuilder come blocco "mediaStrip" per impaginare fasce
 * multimediali nella home e nelle pagine dedicate.
 */

import { useState, type CSSProperties } from "react";
import Image from "next/image"; // Perf: <Image> serve AVIF/WebP, srcset e lazy default
import type { MediaStripBlockData, MediaStripItem } from "@/components/admin/builder/types";
import { toYouTubeEmbed, youtubeThumbnail } from "@/lib/youtube";

function VideoItem({ item }: { item: MediaStripItem }) {
  if (!item.url) return null;
  const embed = toYouTubeEmbed(item.url);
  const [playing, setPlaying] = useState(false);
  const thumb = embed ? youtubeThumbnail(item.url, "hq") : null;

  if (embed && !playing && thumb) {
    // Clicca per far partire l'iframe (risparmia CPU + migliora LCP)
    return (
      <button
        type="button"
        onClick={() => setPlaying(true)}
        aria-label={item.title ? `Riproduci video: ${item.title}` : "Riproduci video"}
        className="relative w-full aspect-video rounded-xl overflow-hidden bg-black group ring-1 ring-neutral-200 hover:ring-[#4a90c4] transition-all"
      >
        {/* Perf: aspect-video fisso sul parent → Image fill + sizes responsive */}
        <Image
          src={thumb}
          alt={item.title || ""}
          fill
          sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
          className="object-cover"
        />
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-[#1a3a5c] ml-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </button>
    );
  }

  if (embed) {
    return (
      <div className="w-full aspect-video rounded-xl overflow-hidden ring-1 ring-neutral-200 bg-black">
        <iframe
          src={`${embed}?autoplay=1&rel=0&modestbranding=1`}
          title={item.title || "Video"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    );
  }

  return (
    <div className="w-full aspect-video rounded-xl overflow-hidden ring-1 ring-neutral-200 bg-black">
      <video src={item.url} controls className="w-full h-full object-cover" />
    </div>
  );
}

function PhotoItem({ item }: { item: MediaStripItem }) {
  if (!item.url) return null;
  const Tag: React.ElementType = item.href ? "a" : "div";
  const props = item.href ? { href: item.href, target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <Tag {...props} className="relative block w-full aspect-[4/3] rounded-xl overflow-hidden ring-1 ring-neutral-200 bg-neutral-100 hover:ring-[#4a90c4] transition-all group">
      {/* Perf: aspect-[4/3] fisso → Image fill con sizes responsive 3-col grid */}
      <Image
        src={item.url}
        alt={item.title || ""}
        fill
        sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
        className="object-cover group-hover:scale-105 transition-transform duration-500"
      />
    </Tag>
  );
}

function GalleryItem({ item }: { item: MediaStripItem }) {
  const imgs = (item.images ?? []).filter(Boolean);
  const [active, setActive] = useState(0);
  if (imgs.length === 0) return null;
  return (
    <div className="w-full rounded-xl overflow-hidden ring-1 ring-neutral-200 bg-neutral-100">
      <a href={imgs[active]} target="_blank" rel="noopener noreferrer" className="relative block w-full aspect-[4/3] overflow-hidden">
        {/* Perf: aspect-[4/3] fisso → Image fill responsive */}
        <Image
          src={imgs[active]}
          alt={item.title || ""}
          fill
          sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
          className="object-cover hover:scale-105 transition-transform duration-500"
        />
      </a>
      {imgs.length > 1 && (
        <div className="flex gap-1 p-1.5 bg-white overflow-x-auto">
          {imgs.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Mostra immagine ${i + 1}${item.title ? ` di ${item.title}` : ""}`}
              aria-pressed={i === active}
              className={`relative flex-shrink-0 w-12 h-12 rounded overflow-hidden ring-2 transition-all ${i === active ? "ring-[#4a90c4]" : "ring-transparent hover:ring-neutral-300"}`}
            >
              {/* Perf: thumb 48x48 fissi (w-12 h-12) */}
              <Image
                src={src}
                alt=""
                width={48}
                height={48}
                sizes="48px"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AudioItem({ item }: { item: MediaStripItem }) {
  if (!item.url) return null;
  const isSpotify = item.url.includes("spotify.com");
  if (isSpotify) {
    const embed = item.url.includes("/embed")
      ? item.url
      : item.url.replace("/episode/", "/embed/episode/").replace("/show/", "/embed/show/");
    return (
      <div className="w-full rounded-xl overflow-hidden ring-1 ring-neutral-200">
        <iframe src={embed} width="100%" height="152" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" />
      </div>
    );
  }
  return (
    <div className="w-full rounded-xl bg-gradient-to-br from-violet-50 to-sky-50 ring-1 ring-neutral-200 p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-lg bg-violet-500/20 flex items-center justify-center shrink-0">
          <span className="text-2xl">🎙️</span>
        </div>
        <div className="min-w-0">
          <div className="font-bold text-sm text-neutral-800 truncate">{item.title || "Audio"}</div>
          {item.caption && <div className="text-xs text-neutral-500 truncate">{item.caption}</div>}
        </div>
      </div>
      <audio src={item.url} controls className="w-full h-10" />
    </div>
  );
}

function RenderItem({ item }: { item: MediaStripItem }) {
  switch (item.type) {
    case "video":   return <VideoItem item={item} />;
    case "photo":   return <PhotoItem item={item} />;
    case "gallery": return <GalleryItem item={item} />;
    case "audio":   return <AudioItem item={item} />;
  }
}

function ItemFrame({ item }: { item: MediaStripItem }) {
  return (
    <figure className="w-full">
      <RenderItem item={item} />
      {(item.title || item.caption) && (
        <figcaption className="mt-2 text-center">
          {item.title && <div className="text-sm font-semibold text-neutral-800 line-clamp-1">{item.title}</div>}
          {item.caption && <div className="text-xs text-neutral-500 line-clamp-2">{item.caption}</div>}
        </figcaption>
      )}
    </figure>
  );
}

export function MediaStripBlock({ title, subtitle, items, layout = "strip", columns = 3, bgColor, className }: MediaStripBlockData["props"]) {
  const [current, setCurrent] = useState(0);
  const visible = (items ?? []).filter((it) => {
    if (it.type === "gallery") return (it.images?.length ?? 0) > 0;
    return !!it.url;
  });
  if (visible.length === 0) return null;

  const wrapperStyle: CSSProperties = bgColor ? { backgroundColor: bgColor } : {};
  const textOnDark = !!bgColor;

  return (
    <section
      className={`py-10 ${bgColor ? "w-full" : ""} ${className ?? ""}`}
      style={wrapperStyle}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {(title || subtitle) && (
          <header className="mb-6 text-center">
            {subtitle && (
              <div className={`text-xs font-bold uppercase tracking-[0.2em] mb-1 ${textOnDark ? "text-white/70" : "text-[#4a90c4]"}`}>
                {subtitle}
              </div>
            )}
            {title && (
              <h2 className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${textOnDark ? "text-white" : "text-[#1a3a5c]"}`}>
                {title}
              </h2>
            )}
          </header>
        )}

        {layout === "strip" && (
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            {visible.map((item, i) => (
              <div key={i} className="flex-shrink-0 w-[85%] sm:w-[45%] md:w-[30%] snap-start">
                <ItemFrame item={item} />
              </div>
            ))}
          </div>
        )}

        {layout === "grid" && (
          <div className={`grid gap-4 ${columns === 2 ? "sm:grid-cols-2" : columns === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
            {visible.map((item, i) => (
              <ItemFrame key={i} item={item} />
            ))}
          </div>
        )}

        {layout === "carousel" && (
          <div className="relative">
            <div className="max-w-3xl mx-auto">
              <ItemFrame item={visible[current]} />
            </div>
            {visible.length > 1 && (
              <div className="mt-4 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrent((c) => (c - 1 + visible.length) % visible.length)}
                  className={`p-2 rounded-full ${textOnDark ? "bg-white/10 hover:bg-white/20 text-white" : "bg-neutral-100 hover:bg-neutral-200 text-neutral-700"}`}
                  aria-label="Precedente"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="flex gap-1.5">
                  {visible.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setCurrent(i)}
                      className={`h-2 rounded-full transition-all ${i === current ? "w-6 " + (textOnDark ? "bg-white" : "bg-[#4a90c4]") : "w-2 " + (textOnDark ? "bg-white/30" : "bg-neutral-300")}`}
                      aria-label={`Slide ${i + 1}`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setCurrent((c) => (c + 1) % visible.length)}
                  className={`p-2 rounded-full ${textOnDark ? "bg-white/10 hover:bg-white/20 text-white" : "bg-neutral-100 hover:bg-neutral-200 text-neutral-700"}`}
                  aria-label="Successivo"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

